import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Spacer,
} from '@chakra-ui/react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { ChevronUpOutline } from '../../../design-system/icon';
import { Heading } from '../../../design-system/typography';
import { Bizy } from '../../bizy';
import { ErrorBoundary } from '../../error/error-boundary';
import { DraftAssistProp } from './hooks/use-draft-assist';
import { Dimension, ScoreBoxAccordionContent } from './scorebox-components';

type DraftAssistAccordionProps = {
	dimensions: Dimension[];
	isLoading: boolean;
	isScoring?: boolean;
	text: string;
	draftAssist?: DraftAssistProp;
	hasUnmetDimension?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	onDimensionExpanded?: (key: string | null) => void;
};

export const DraftAssistAccordion: FC<DraftAssistAccordionProps> = ({
	dimensions,
	isLoading,
	isScoring,
	text,
	draftAssist,
	hasUnmetDimension,
	onOpenChange,
	onDimensionExpanded,
}) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	// Always starts closed so the LLM only fires on explicit user intent.
	// Session state resets on each modal open — no localStorage restore.
	const [isOpen, setIsOpen] = useState(false);
	const { track } = useAnalytics();
	const matches = dimensions.filter((d) => d.matches).length;

	const bizyState = useMemo(() => (isLoading || isScoring ? 'thinking' : 'curious'), [isLoading, isScoring]);

	return (
		<ErrorBoundary>
			<Accordion
				allowToggle
				index={isOpen ? 0 : -1}
				onChange={(index) => {
					const nextIsOpen = index === 0;

					if (nextIsOpen) {
						track(AnalyticsEvent.GratitudeGauge.Opened, { type: 'accordion' });
					} else {
						track(AnalyticsEvent.GratitudeGauge.Closed, { type: 'accordion' });
					}

					setIsOpen(nextIsOpen);
					onOpenChange?.(nextIsOpen);
				}}
			>
				<AccordionItem borderBottom="none">
					{({ isExpanded }) => (
						<>
							<AccordionButton color="text.default" columnGap={2} justifyContent="center" py={{ base: 3, sm: 2 }}>
								<Popover isOpen={hasUnmetDimension && !isExpanded} placement="top" closeOnBlur={false} isLazy>
									<PopoverTrigger>
										<Box as="span" display="inline-block" flexShrink={0}>
											<Bizy state={bizyState} size="md" />
										</Box>
									</PopoverTrigger>
									<PopoverContent width="auto" maxW="160px" pointerEvents="none">
										<PopoverArrow />
										<PopoverBody fontSize="xs" textAlign="center" px={3} py={2}>
											{t('draftAssistHint')}
										</PopoverBody>
									</PopoverContent>
								</Popover>
								<Heading>{t('bizyTitle')}</Heading>
								<Box color="text.onColor" fontSize="sm" fontWeight="medium" borderRadius="md" px={2} bg="purple.300" letterSpacing="wider">
									{matches}/{dimensions.length}
								</Box>
								<Spacer />
								<ChevronUpOutline transitionProperty="transform" transitionDuration="fast" transform={isExpanded ? 'none' : 'rotate(180deg)'} />
							</AccordionButton>

							<AccordionPanel p={3}>
								<ScoreBoxAccordionContent
									dimensions={dimensions}
									isLoading={isLoading}
									text={text}
									draftAssist={draftAssist}
									showCupFiller={false}
									showExamples={false}
									onDimensionExpanded={onDimensionExpanded}
								/>
							</AccordionPanel>
						</>
					)}
				</AccordionItem>
			</Accordion>
		</ErrorBoundary>
	);
};
